import { useState, useRef, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDateForDisplay } from '../../utils/dateUtils';

interface DateFilterSelectorProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
}

export function DateFilterSelector({ startDate, endDate, onStartDateChange, onEndDateChange }: DateFilterSelectorProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'date' | 'period'>('date');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDisplayText = () => {
    if (!startDate && !endDate) {
      return t.posts.filterByDate;
    }
    if (mode === 'date' && startDate) {
      return formatDateForDisplay(startDate);
    }
    if (mode === 'period' && startDate && endDate) {
      return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
    }
    if (mode === 'period' && startDate) {
      return `${formatDateForDisplay(startDate)} - ...`;
    }
    return t.posts.filterByDate;
  };

  const handleModeChange = (newMode: 'date' | 'period') => {
    setMode(newMode);
    if (newMode === 'date') {
      onEndDateChange('');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-full h-9 gap-2 flex-shrink-0"
        style={{
          backgroundColor: 'var(--color-background)',
          color: 'var(--color-text-primary)',
          border: `1px solid var(--color-border)`,
        }}
      >
        <Calendar className="w-4 h-4" />
        <span className="text-xs">{getDisplayText()}</span>
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute top-full right-0 mt-2 w-64 backdrop-blur-md rounded-2xl shadow-xl z-50 p-6"
          style={{
            backgroundColor: 'var(--color-surface)',
            border: `1px solid var(--color-border)`,
          }}
        >
          {/* Mode toggle */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={mode === 'date' ? 'default' : 'outline'}
              onClick={() => handleModeChange('date')}
              className="flex-1 rounded-full h-9"
              size="sm"
              style={mode === 'date' ? {
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-text-light)',
              } : {
                backgroundColor: 'var(--color-background)',
                color: 'var(--color-text-primary)',
                border: `1px solid var(--color-border)`,
              }}
            >
              {t.posts.date}
            </Button>
            <Button
              variant={mode === 'period' ? 'default' : 'outline'}
              onClick={() => handleModeChange('period')}
              className="flex-1 rounded-full h-9"
              size="sm"
              style={mode === 'period' ? {
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-text-light)',
              } : {
                backgroundColor: 'var(--color-background)',
                color: 'var(--color-text-primary)',
                border: `1px solid var(--color-border)`,
              }}
            >
              {t.posts.period}
            </Button>
          </div>

          {/* Date inputs */}
          {mode === 'date' ? (
            <div className="space-y-2">
              <Label className="text-xs" style={{ color: 'var(--color-text-primary)' }}>{t.posts.date}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                className="rounded-full text-xs h-9 w-full"
                style={{
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text-primary)',
                  border: `1px solid var(--color-border)`,
                }}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs" style={{ color: 'var(--color-text-primary)' }}>{t.posts.startDate}</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => onStartDateChange(e.target.value)}
                  className="rounded-full text-xs h-9 w-full"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    color: 'var(--color-text-primary)',
                    border: `1px solid var(--color-border)`,
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs" style={{ color: 'var(--color-text-primary)' }}>{t.posts.endDate}</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                  className="rounded-full text-xs h-9 w-full"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    color: 'var(--color-text-primary)',
                    border: `1px solid var(--color-border)`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Clear button */}
          <div className="mt-3 pt-3" style={{ borderTop: `1px solid var(--color-border)` }}>
            <Button
              variant="ghost"
              onClick={() => {
                onStartDateChange('');
                onEndDateChange('');
                setIsOpen(false);
              }}
              className="w-full rounded-full h-9"
              size="sm"
              style={{
                color: 'var(--color-text-primary)',
              }}
            >
              {t.posts.clear}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}